import { ref, computed, watch } from 'vue'

export type HistoriqueModeKind = 'off' | 'all' | 'annee'

const STORAGE_KEY = 'mydit_historique_context'

type PersistedHistorique = {
	mode: HistoriqueModeKind
	anneeUniversitaire: string | null
}

/**
 * Contexte historique GLOBAL (partagé entre pages, persisté).
 * - off  : sessions prévu + en cours (défaut API)
 * - all  : include_historique=1 (tout l'historique, comme avant)
 * - annee: annee_universitaire=YYYY/YYYY (filtre une année)
 */
const mode = ref<HistoriqueModeKind>('off')
const anneeUniversitaire = ref<string | null>(null)
const modalOpen = ref(false)
let hydrated = false

function loadFromStorage() {
	if (hydrated || typeof window === 'undefined') return
	hydrated = true
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return
		const parsed = JSON.parse(raw) as PersistedHistorique
		if (parsed.mode === 'all' || parsed.mode === 'off' || parsed.mode === 'annee') {
			mode.value = parsed.mode
			anneeUniversitaire.value =
				parsed.mode === 'annee' && parsed.anneeUniversitaire
					? parsed.anneeUniversitaire
					: null
			if (mode.value === 'annee' && !anneeUniversitaire.value) {
				mode.value = 'off'
			}
		}
	} catch {
		/* ignore */
	}
}

function persist() {
	if (typeof window === 'undefined') return
	const payload: PersistedHistorique = {
		mode: mode.value,
		anneeUniversitaire: mode.value === 'annee' ? anneeUniversitaire.value : null,
	}
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
	} catch {
		/* ignore */
	}
}

loadFromStorage()

watch([mode, anneeUniversitaire], () => persist())

export function useHistoriqueMode() {
	loadFromStorage()

	/** true si historique actif (all ou annee) — rétrocompat v-model / watchers */
	const enabled = computed({
		get: () => mode.value !== 'off',
		set: (v: boolean) => {
			if (!v) {
				setOff()
				return
			}
			// Deep-link / v-model=true : activer « tout » seulement depuis off.
			// Si déjà en année / all, ne pas écraser le mode courant.
			if (mode.value === 'off') setAll()
		},
	})

	const queryParams = computed(() => {
		if (mode.value === 'all') return { include_historique: '1' } as Record<string, string>
		if (mode.value === 'annee' && anneeUniversitaire.value) {
			return { annee_universitaire: anneeUniversitaire.value } as Record<string, string>
		}
		return {} as Record<string, string>
	})

	const label = computed(() => {
		if (mode.value === 'all') return 'Historique — tout'
		if (mode.value === 'annee' && anneeUniversitaire.value) {
			return `${anneeUniversitaire.value} + actives`
		}
		return ''
	})

	/** Clé à watcher sur les pages (changement all ↔ année ↔ off) */
	const contextKey = computed(
		() => `${mode.value}:${anneeUniversitaire.value || ''}`
	)

	function appendToParams(params: URLSearchParams) {
		if (mode.value === 'all') {
			params.set('include_historique', '1')
			params.delete('annee_universitaire')
		} else if (mode.value === 'annee' && anneeUniversitaire.value) {
			params.set('annee_universitaire', anneeUniversitaire.value)
			params.delete('include_historique')
		}
	}

	/** Options à passer aux fetch listes */
	function fetchOptions(): { includeHistorique: boolean; anneeUniversitaire: string | null } {
		return {
			includeHistorique: mode.value === 'all',
			anneeUniversitaire: mode.value === 'annee' ? anneeUniversitaire.value : null,
		}
	}

	function setOff() {
		mode.value = 'off'
		anneeUniversitaire.value = null
	}

	function setAll() {
		mode.value = 'all'
		anneeUniversitaire.value = null
	}

	function selectAnnee(annee: string) {
		mode.value = 'annee'
		anneeUniversitaire.value = annee
	}

	function openModal() {
		modalOpen.value = true
	}

	function closeModal() {
		modalOpen.value = false
	}

	/** Rétrocompat : flip all/off (sans année) */
	function toggle() {
		if (mode.value === 'off') setAll()
		else setOff()
	}

	function setEnabled(v: boolean) {
		enabled.value = v
	}

	return {
		mode,
		anneeUniversitaire,
		enabled,
		label,
		contextKey,
		modalOpen,
		queryParams,
		appendToParams,
		fetchOptions,
		toggle,
		setEnabled,
		setOff,
		setAll,
		selectAnnee,
		openModal,
		closeModal,
	}
}
